// Presentation labels only (Spanish UI). No business rule lives here: which
// action is allowed, what state a Distribution is in and why are decided by
// the API; these maps only name what the server says.

import type { BadgeTone } from "@/ui/badge";
import type {
  CompatibilityEvidence,
  CompatibilityStatus,
  CreativeProduct,
  EffectiveState,
  MembershipRole,
  PlatformType,
  VersionState,
} from "@/api/types";

/**
 * Membership roles in neutral product language. AGENCY_ADMIN is the technical
 * workspace-administrator role of the current model; the UI never presents
 * the customer as an "agency" (owner decision 2026-10-16).
 */
export const ROLE_LABELS: Record<MembershipRole, string> = {
  AGENCY_ADMIN: "Administrador",
  CREATIVE_MANAGER: "Responsable creativo",
  CREATIVE: "Creativo",
  ANALYST: "Analista",
  CLIENT_ADMIN: "Aprobador del cliente",
  VIEWER: "Lector",
};

/** Commercial products V1 (PRD-V1 §1) — names only, no capability claims. */
export const PRODUCTS: Record<CreativeProduct, { label: string; description: string }> = {
  RICH_MEDIA_STANDARD: {
    label: "Rich Media",
    description: "Anuncio interactivo con componentes, animaciones y llamadas a la acción.",
  },
  VIDEO_RICH_MEDIA: {
    label: "Video Rich Media",
    description: "Un video que ya tienes, integrado en un creativo interactivo.",
  },
  ADVERGAMING: { label: "Advergaming", description: "Un juego de marca que se juega dentro del anuncio." },
  SURVEY_RICH_MEDIA: {
    label: "Survey Rich Media",
    description: "Una encuesta breve dentro del anuncio (máximo 8 preguntas por participante).",
  },
};

/**
 * Advertising platform types (7.6F.2): classification only — a type never
 * implies that the platform accepts third-party tags or any InteractAds product.
 */
export const PLATFORM_TYPE_LABELS: Record<PlatformType, string> = {
  DSP: "DSP",
  AD_SERVER: "Ad server",
  AD_NETWORK: "Red publicitaria",
  PUBLISHER: "Medio / publisher",
  SOCIAL: "Red social",
  RETAIL_MEDIA: "Retail media",
  OTHER: "Otra plataforma",
};

/**
 * Compatibility as the SERVER assessed it (platform × product × format, with
 * evidence). Careful wording: InteractAds' own evidence, never a claim about
 * every account, buying workflow or the platform's certification.
 */
export const COMPATIBILITY_STATES: Record<
  CompatibilityStatus,
  { label: string; tone: BadgeTone; formatLabel: (format: string) => string }
> = {
  VERIFIED: {
    label: "Verificado por InteractAds",
    tone: "success",
    formatLabel: (f) => `Verificado para ${f}`,
  },
  LIMITED: {
    label: "Verificado con limitaciones",
    tone: "warning",
    formatLabel: (f) => `${f}: verificado con limitaciones`,
  },
  UNVERIFIED: {
    label: "Compatibilidad aún no verificada",
    tone: "neutral",
    formatLabel: (f) => `${f}: compatibilidad aún no verificada`,
  },
  UNSUPPORTED: {
    label: "No compatible en nuestra prueba",
    tone: "danger",
    formatLabel: (f) => `${f}: no compatible en nuestra prueba`,
  },
};

export const EVIDENCE_SOURCES: Record<CompatibilityEvidence["sourceType"], string> = {
  INTERACTADS_TEST: "Prueba de InteractAds",
  PLATFORM_DOCUMENTATION: "Documentación de la plataforma",
  CUSTOMER_CONFIRMED: "Confirmado por un cliente",
  INTERNAL_REVIEW: "Revisión interna de InteractAds",
};

/** `300x600` → `300×600` for display (the stored value is unchanged). */
export const displayFormat = (format: string) => (/^\d+x\d+$/i.test(format) ? format.replace(/x/i, "×") : format);

export const VERSION_STATES: Record<VersionState, { label: string; tone: BadgeTone }> = {
  DRAFT: { label: "Borrador", tone: "neutral" },
  PREVIEW: { label: "En revisión", tone: "info" },
  CLIENT_REVIEW: { label: "Revisión del cliente", tone: "info" },
  APPROVED: { label: "Aprobada", tone: "brand" },
  PUBLISHED: { label: "Publicada", tone: "success" },
  ARCHIVED: { label: "Archivada", tone: "neutral" },
};

/** Verbs for transitions the server lists in `allowedTransitions`. */
export const TRANSITION_ACTIONS: Record<Exclude<VersionState, "DRAFT">, string> = {
  PREVIEW: "Enviar a revisión",
  CLIENT_REVIEW: "Enviar al cliente",
  APPROVED: "Aprobar",
  PUBLISHED: "Publicar",
  ARCHIVED: "Archivar versión",
};

export const SERVING_STATES: Record<EffectiveState, { label: string; tone: BadgeTone }> = {
  SERVING: { label: "Activa", tone: "success" },
  PAUSED: { label: "Pausada", tone: "warning" },
  SUSPENDED: { label: "Suspendida", tone: "danger" },
  NOT_READY: { label: "Sin publicar", tone: "neutral" },
  ARCHIVED: { label: "Archivada", tone: "neutral" },
};

export const COMMON_FORMATS = ["300x250", "300x600", "728x90", "160x600", "320x50", "320x480", "970x250"] as const;

const AUDIT_ENTITIES: Record<string, string> = {
  client: "Cliente",
  brand: "Marca",
  campaign: "Campaña",
  creative: "Creativo",
  creative_version: "Versión",
  distribution: "Distribución",
  tag: "Tag",
  serving_hold: "Retención de entrega",
  organization_platform: "Plataforma personalizada",
  platform_review_request: "Solicitud de revisión de plataforma",
  membership: "Miembro",
  membership_scope: "Alcance de miembro",
  invitation: "Invitación",
  invitation_scope: "Alcance de invitación",
  organization: "Espacio de trabajo",
};

export const auditEntityLabel = (entityType: string) => AUDIT_ENTITIES[entityType] ?? entityType;

const AUDIT_VERBS: Record<string, string> = {
  created: "creado",
  updated: "actualizado",
  changed: "modificado",
  removed: "eliminado",
  state_changed: "cambio de estado",
  draft_updated: "borrador editado",
  active_version_changed: "versión activa cambiada",
  paused: "pausada",
  resumed: "reanudada",
  archived: "archivada",
  released: "liberada",
  accepted: "aceptada",
  revoked: "revocada",
  organization_self_provisioned: "espacio de trabajo creado",
  organization_created: "organización creada",
  admin_hold_placed: "retención administrativa",
  admin_hold_released: "retención administrativa liberada",
  custom_platform_mapped: "revisada por InteractAds",
};

export function auditActionLabel(action: string) {
  const [, verb = action] = action.split(".");
  return AUDIT_VERBS[verb] ?? verb.replace(/_/g, " ");
}
